import { NoopScrollStrategy } from "@angular/cdk/overlay";
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import * as moment from "moment";
import { ReturnApiService } from "src/app/api/return-api.service";
import { OVERVIEW_DATEPICKER_FORMATS } from "src/app/global/date-format";
import { LensConfigMode, LensSectionMode, LensSide, LensType } from "src/app/model/configuration/configuration";
import { ArticleType, LensOrderItem, OrderItem } from "src/app/model/order/order";
import { NewOrderStoreService } from "src/app/new-order/new-order-store-service.service";
import { ReturnStoreService } from "src/app/return/return-store.service";
import { i18nService } from "src/app/service-utils/i18n.service";
import { LanguagePickerService } from "src/app/utils/language-picker/language-picker.service";
import { HomeOrdersDialogComponent } from "../home-orders-dialog/home-orders-dialog.component";
import { HomeOrdersService } from "../home-orders.service";

@Component({
  selector: 'app-home-orders-copy-dialog',
  templateUrl: './home-orders-copy-dialog.component.html',
  styleUrls: ['./home-orders-copy-dialog.component.scss'],
  providers: [
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: OVERVIEW_DATEPICKER_FORMATS},
  ]
})
export class HomeOrdersCopyDialogComponent implements OnInit, AfterViewChecked {
  originalItems: LensOrderItem[];
  items: MatTableDataSource<LensOrderItem>;
  selectedRows: LensOrderItem[] = []; //? is copyPossible(items) {check conditions}
  displayedColumns: string[] = ['checkbox', 'orderDate', 'side', 'article', 'commission', 'chargeNum', 'return', 'actions'];

  searchForm: FormGroup;

  hintKeys = ["", "home.orders.hints.twoSameSide", "home.orders.hints.threeSelected", "home.orders.hints.softSelected", "home.orders.hints.notCopyPossible"];
  hintMessage: string;
  hintIndex: number;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('overviewPaginator', {static: true}) overviewPaginator: MatPaginator;

  isLoading = false;

  constructor(
    private homeOrderService: HomeOrdersService,
    private i18nService: i18nService,
    private languagePickerService: LanguagePickerService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<HomeOrdersCopyDialogComponent>,
    private cdRef: ChangeDetectorRef,
    private returnApi: ReturnApiService,
    private router: Router,
    private returnStoreService: ReturnStoreService,
    private newOrderStoreService: NewOrderStoreService,
    private formBuilder: FormBuilder,
    private dateAdapter: DateAdapter<Date>
  ) {
  }

  ngOnInit(): void {
    this.fetchOrders();

    this.languagePickerService.currentLanguage$.subscribe(() => {
      if(this.languagePickerService.isEnglish()) {
        this.dateAdapter.setLocale('en')
      } else {
        this.dateAdapter.setLocale('de');
      }
    });

    this.searchForm = this.formBuilder.group({
      deliveryNmr: [''],
      commission: [''],
      chargeNum: [''],
      dateFrom: [''],
      dateTo: ['']
    });
  }

  public fetchOrders() {
    this.isLoading = true;
    this.homeOrderService.getHomeOrders({
      "count": 10_000,
      "query": {},
      "startIndex": 0
    }).subscribe(response => {
      this.originalItems = response.result.filter(item => item.article.type === ArticleType.LENS).map(item => this.asLensItem(item));
      if(this.newOrderStoreService.getLensSectionMode() === LensSectionMode.TRIAL_LENS_COPY) {
        this.originalItems = this.originalItems.filter(item => item.article.lensType === LensType.HARD);
      }
      this.items = new MatTableDataSource(this.originalItems);
      if (this.items != undefined) {
        this.createSortAndPagintaion();
      }
      this.isLoading = false;
    });
  }

  ngAfterViewChecked() {
    this.hintMessage = this.hintKeys[this.hintIndex];
    this.cdRef.detectChanges();
  }

  search(event: any) {
    event.preventDefault();
    let filterOrderItems = this.originalItems;

    if(this.searchForm.get('deliveryNmr').value && this.searchForm.get('deliveryNmr').value !== '') {
      filterOrderItems = filterOrderItems.filter(orderItem => orderItem.deliveryNumber?.includes(this.searchForm.get('deliveryNmr').value));
    };

    if(this.searchForm.get('commission').value && this.searchForm.get('commission').value !== '') {
      filterOrderItems = filterOrderItems.filter(orderItem => orderItem.article.type === ArticleType.LENS)
                                         .map(orderItem => this.asLensItem(orderItem))
                                         .filter(orderItem => orderItem.commission?.includes(this.searchForm.get('commission').value));
    };

    if(this.searchForm.get('chargeNum').value && this.searchForm.get('chargeNum').value !== '') {
      filterOrderItems = filterOrderItems.filter(orderItem => orderItem.chargeNumber?.includes(this.searchForm.get('chargeNum').value));
    };

    if(this.searchForm.get('dateFrom').value && this.searchForm.get('dateFrom').value !== '') {
      filterOrderItems = filterOrderItems.filter(orderItem =>
        orderItem.orderDate && moment(new Date(orderItem.orderDate.toISOString().substring(0, 10))).format('YYYY-MM-DD') >= this.searchForm.get('dateFrom').value.format('YYYY-MM-DD')
      );
    }

    if(this.searchForm.get('dateTo').value && this.searchForm.get('dateTo').value !== '') {
      filterOrderItems = filterOrderItems.filter(orderItem =>
        orderItem.orderDate && moment(new Date(orderItem.orderDate.toISOString().substring(0, 10))).format('YYYY-MM-DD') <= this.searchForm.get('dateTo').value.format('YYYY-MM-DD')
      );
    }

    this.items = new MatTableDataSource(filterOrderItems);
    this.createSortAndPagintaion();
  }

  pickLastNMonths(months: number) {
    this.searchForm.patchValue({
      dateFrom: moment().subtract(months, "months"),
      dateTo: moment(),
    });
  }

  asLensItem(item: OrderItem): LensOrderItem {
    return item as LensOrderItem;
  }

  isEnglish(): boolean {
    return this.languagePickerService.getLanguageCode() === 'en';
  }

  isNeedCheck(item: OrderItem) {
    return (this.selectedRows.findIndex(selectedRow => selectedRow.orderItemId === item.orderItemId) > -1) ? true : false;
  }

  getOrderDate(date: Date): string {
    return this.i18nService.toDateString(date);
  }

  getArticle(item: OrderItem) {
    return this.i18nService.getLensesDescriptionOverview(item);
  }

  select(item: LensOrderItem) {
    let isPresent = false;
    if (this.selectedRows.length > 0) {
      for (let row of this.selectedRows) {
        if (item == row) {
          isPresent = true
        }
      }
    }
    if (isPresent) {
      this.selectedRows = this.selectedRows.filter(obj => obj !== item);
    } else {

      if(this.selectedRows.findIndex(selectedRow => selectedRow.side === item.side) > -1) {
        this.selectedRows = this.selectedRows.filter(obj => obj.side !== item.side);
      }

      this.selectedRows.push(item);
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  copy() {
    this.dialogRef.close();
    if(this.newOrderStoreService.getLensSectionMode() === LensSectionMode.FINAL_LENS_COPY) {
      this.copyFinalLens();
    } else {
      this.copyTrialLens();
    }
  }

  copyFinalLens() {
    if(this.selectedRows.length === 2) {
      this.newOrderStoreService.changeLensConfigMode(LensConfigMode.DUAL);
      this.newOrderStoreService.changeIsRightSideEnable(true);
      this.newOrderStoreService.changeIsLeftSideEnable(true);
      if(this.selectedRows[0].side === LensSide.RIGHT) {
        this.selectedRows[1].commission = this.selectedRows[0].commission;
        this.newOrderStoreService.setCopyFinalLensRight(this.selectedRows[0]);
        this.newOrderStoreService.setCopyFinalLensLeft(this.selectedRows[1]);
      } else if(this.selectedRows[0].side === LensSide.LEFT) {
        this.selectedRows[0].commission = this.selectedRows[1].commission;
        this.newOrderStoreService.setCopyFinalLensRight(this.selectedRows[1]);
        this.newOrderStoreService.setCopyFinalLensLeft(this.selectedRows[0]);
      }
    } else {
      this.newOrderStoreService.changeLensConfigMode(LensConfigMode.SINGLE);
      if(this.selectedRows[0].side === LensSide.RIGHT) {
        this.newOrderStoreService.changeIsRightSideEnable(true);
        this.newOrderStoreService.changeIsLeftSideEnable(false);
        this.newOrderStoreService.setCopyFinalLensRight(this.selectedRows[0]);
      } else {
        this.newOrderStoreService.changeIsLeftSideEnable(true);
        this.newOrderStoreService.changeIsRightSideEnable(false);
        this.newOrderStoreService.setCopyFinalLensLeft(this.selectedRows[0]);
      }
    }
    this.newOrderStoreService.changeLensSectionMode(LensSectionMode.FINAL_LENS);
    document.querySelector('#final-lens').scrollIntoView();
    this.router.navigate(['/new-order'], {fragment: 'final-lens'});
  }

  copyTrialLens() {
    if(this.selectedRows.length === 2) {
      this.newOrderStoreService.setLensConfigMode(LensConfigMode.DUAL);
      this.newOrderStoreService.changeIsLeftSideEnable(true);
      this.newOrderStoreService.changeIsRightSideEnable(true);
      if(this.selectedRows[0].side === LensSide.RIGHT) {
        this.selectedRows[1].commission = this.selectedRows[0].commission;
        this.newOrderStoreService.setCopyTrialLensRight(this.selectedRows[0]);
        this.newOrderStoreService.setCopyTrialLensLeft(this.selectedRows[1]);
      } else if(this.selectedRows[0].side === LensSide.LEFT) {
        this.selectedRows[0].commission = this.selectedRows[1].commission;
        this.newOrderStoreService.setCopyTrialLensRight(this.selectedRows[1]);
        this.newOrderStoreService.setCopyTrialLensLeft(this.selectedRows[0]);
      }
    } else {
      this.newOrderStoreService.setLensConfigMode(LensConfigMode.SINGLE);
      if(this.selectedRows[0].side === LensSide.RIGHT) {
        this.newOrderStoreService.changeIsRightSideEnable(true);
        this.newOrderStoreService.changeIsLeftSideEnable(false);
        this.newOrderStoreService.setCopyTrialLensRight(this.selectedRows[0]);
      } else {
        this.newOrderStoreService.changeIsLeftSideEnable(true);
        this.newOrderStoreService.changeIsRightSideEnable(false);
        this.newOrderStoreService.setCopyTrialLensLeft(this.selectedRows[0]);
      }
    }
    this.newOrderStoreService.changeLensSectionMode(LensSectionMode.TRIAL_LENS);
    document.querySelector('#trial-lens').scrollIntoView();
    this.router.navigate(['/new-order'], {fragment: 'trial-lens'});
  }

  openInfoDialog(item: OrderItem): void {
    this.dialog.open(HomeOrdersDialogComponent, {
      width: '400px',
      data: {item: item},
      scrollStrategy: new NoopScrollStrategy()
    });
  }

  createSortAndPagintaion() {
    this.items.sortingDataAccessor = (item, property) => {
      switch(property) {
        case 'article': return this.getArticle(item);
        default: return item[property];
      }
    };

    this.items.sort = this.sort;
    this.items.paginator = this.overviewPaginator;
  }

  getSide(item: any): string {
    return (item.side) ? item.side.charAt(0) : '';
  }

}
